// Localização: frontend/src/components/Prontuario/PacienteDocumentacaoTab.jsx (versão 0.2.0)
// Componente principal da aba de Documentação do Paciente
// ✅ NOVO: Integrado FloatingCalendarFilter para filtros de data

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { FileText, Upload, Eye, Download, Edit, Trash2, Search, Filter, File, Grid3x3, List, ArrowUpDown, ArrowUp, ArrowDown, Calendar, Link2, ChevronDown } from 'lucide-react';
import Alert from '../UI/Alert';
import LoadingIndicator from '../Common/LoadingIndicator';
import Card from '../UI/Card';
import PrimaryButton from '../Common/PrimaryButton';
import Modal from '../UI/Modal';
import ConfirmacaoModal from '../Common/ConfirmacaoModal';
import PeriodoFilterButton from './PeriodoFilterButton';
import DropdownMenu from '../UI/DropdownMenu';
import { useDocumentosPaciente } from '../../hooks/data/useDocumentosPaciente';
import { visualizarDocumentoPaciente, downloadDocumentoPaciente } from '../../api/api';
import { useNotification } from '../../context/NotificationContext';
import UploadDocumentoModal from './Modals/UploadDocumentoModal';
import EditarDocumentoModal from './Modals/EditarDocumentoModal';

const CATEGORIAS = [
    { value: '', label: 'Todas' },
    { value: 'exame', label: 'Exames' },
    { value: 'receita', label: 'Receitas' },
    { value: 'atestado', label: 'Atestados' },
    { value: 'historico-medico', label: 'Histórico Médico' },
    { value: 'consentimento', label: 'Consentimentos' },
    { value: 'identidade', label: 'Identidade' },
    { value: 'outro', label: 'Outros' }
];

const ORDENACOES = [
    { value: 'data_desc', label: 'Data (mais recente)', field: 'data_upload', order: 'desc' },
    { value: 'data_asc', label: 'Data (mais antigo)', field: 'data_upload', order: 'asc' },
    { value: 'titulo_asc', label: 'Título (A-Z)', field: 'titulo', order: 'asc' },
    { value: 'titulo_desc', label: 'Título (Z-A)', field: 'titulo', order: 'desc' },
    { value: 'tamanho_desc', label: 'Tamanho (maior)', field: 'tamanho_bytes', order: 'desc' },
    { value: 'tamanho_asc', label: 'Tamanho (menor)', field: 'tamanho_bytes', order: 'asc' }
];

const PacienteDocumentacaoTab = ({ pacienteId }) => {
    const { success: showSuccess, error: showError } = useNotification();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedDocumento, setSelectedDocumento] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
    const [ordenacao, setOrdenacao] = useState('data_desc');
    const [periodo, setPeriodo] = useState(''); // Período do FloatingCalendarFilter
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filtros, setFiltros] = useState({
        categoria: '',
        search: ''
    });

    // Handler para mudança de período e datas
    const handleDateRangeChange = useCallback((newStartDate, newEndDate) => {
        setStartDate(newStartDate || '');
        setEndDate(newEndDate || '');
    }, []);

    const handlePeriodoChange = useCallback((newPeriodo) => {
        setPeriodo(newPeriodo || '');
    }, []);

    // ✅ CORREÇÃO: Usar useMemo para criar objeto de filtros estável
    const filtrosHook = useMemo(() => {
        const obj = {
            page: 1,
            limit: 50
        };
        
        if (filtros.categoria) {
            obj.categoria = filtros.categoria;
        }
        
        if (filtros.search && filtros.search.trim()) {
            obj.search = filtros.search.trim();
        }
        
        if (startDate) {
            obj.startDate = startDate;
        }
        
        if (endDate) {
            obj.endDate = endDate;
        }
        
        return obj;
    }, [filtros.categoria, filtros.search, startDate, endDate]);

    const { documentos, paginacao, isLoading, error, refetch, uploadDocumento, atualizarDocumento, deletarDocumento } = 
        useDocumentosPaciente(pacienteId, filtrosHook);

    // ✅ CORREÇÃO: Garantir que paginacao sempre tenha valores padrão
    const paginacaoSafe = paginacao || {
        total: 0,
        page: 1,
        limit: 50,
        totalPages: 0
    };

    // Filtrar, ordenar e processar documentos
    // ✅ NOTA: Os documentos já vêm filtrados por data, categoria e busca do backend
    // Aqui apenas aplicamos a ordenação local
    const documentosFiltrados = useMemo(() => {
        let resultado = [...documentos];

        // Os filtros de busca, categoria e data já são aplicados no backend
        // Aqui aplicamos apenas a ordenação local

        // Aplicar ordenação
        const ordenacaoConfig = ORDENACOES.find(o => o.value === ordenacao);
        if (ordenacaoConfig) {
            resultado.sort((a, b) => {
                let aVal = a[ordenacaoConfig.field];
                let bVal = b[ordenacaoConfig.field];

                // Tratamento especial para diferentes tipos de dados
                if (ordenacaoConfig.field === 'data_upload') {
                    aVal = aVal ? new Date(aVal).getTime() : 0;
                    bVal = bVal ? new Date(bVal).getTime() : 0;
                } else if (ordenacaoConfig.field === 'titulo') {
                    aVal = (aVal || '').toLowerCase();
                    bVal = (bVal || '').toLowerCase();
                } else if (ordenacaoConfig.field === 'tamanho_bytes') {
                    aVal = aVal || 0;
                    bVal = bVal || 0;
                }

                // Comparação
                if (aVal < bVal) return ordenacaoConfig.order === 'asc' ? -1 : 1;
                if (aVal > bVal) return ordenacaoConfig.order === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return resultado;
    }, [documentos, filtros.search, ordenacao]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const kb = bytes / 1024;
        const mb = kb / 1024;
        if (mb >= 1) {
            return `${mb.toFixed(2)} MB`;
        }
        return `${kb.toFixed(2)} KB`;
    };

    const getCategoriaLabel = (categoria) => {
        const cat = CATEGORIAS.find(c => c.value === categoria);
        return cat ? cat.label : categoria || 'Sem categoria';
    };

    const getEntidadeOrigemLabel = (entidadeOrigem) => {
        const labels = {
            'historico_medico': 'Histórico Médico',
            'alergia': 'Alergia',
            'vacina': 'Vacina',
            'exame': 'Exame',
            'historico_familiar': 'Histórico Familiar'
        };
        return labels[entidadeOrigem] || entidadeOrigem || '';
    };

    const handleUpload = async (formData) => {
        try {
            await uploadDocumento(formData);
            setIsUploadModalOpen(false);
            showSuccess('Documento enviado com sucesso!');
        } catch (error) {
            showError(error?.response?.data?.message || 'Erro ao enviar documento.');
        }
    };

    const handleEdit = async (documentoId, metadados) => {
        try {
            await atualizarDocumento(documentoId, metadados);
            setIsEditModalOpen(false);
            setSelectedDocumento(null);
            showSuccess('Documento atualizado com sucesso!');
        } catch (error) {
            showError(error?.response?.data?.message || 'Erro ao atualizar documento.');
        }
    };

    const handleDelete = async () => {
        if (!selectedDocumento) return;
        try {
            await deletarDocumento(selectedDocumento.id);
            setIsDeleteModalOpen(false);
            setSelectedDocumento(null);
            showSuccess('Documento deletado com sucesso!');
        } catch (error) {
            showError(error?.response?.data?.message || 'Erro ao deletar documento.');
        }
    };

    const handleView = (documento) => {
        setSelectedDocumento(documento);
        setIsViewModalOpen(true);
    };

    const handleDownload = async (documento) => {
        try {
            const response = await downloadDocumentoPaciente(pacienteId, documento.id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documento.nome_arquivo);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showSuccess('Download iniciado!');
        } catch (error) {
            showError('Erro ao fazer download do documento.');
        }
    };

    const handleOpenEdit = (documento) => {
        setSelectedDocumento(documento);
        setIsEditModalOpen(true);
    };

    const handleOpenDelete = (documento) => {
        setSelectedDocumento(documento);
        setIsDeleteModalOpen(true);
    };

    if (isLoading) {
        return <LoadingIndicator message="Carregando documentos..." />;
    }

    return (
        <div className="space-y-4 relative">
            {error && <Alert type="error" message={error} />}

            {/* Header com ações */}
            <div className="flex flex-col gap-3">
                {/* Primeira linha: Busca, Filtros e Upload */}
                <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
                    <div className="flex-1 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        {/* Busca */}
                        <div className="relative flex-1 sm:flex-initial sm:w-64">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Buscar documentos..."
                                value={filtros.search}
                                onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                maxLength={200}
                            />
                        </div>

                        {/* Filtro por categoria - DropdownMenu */}
                        <div className="flex-1 sm:flex-initial sm:w-48">
                            <DropdownMenu
                                trigger={
                                    <button
                                        className={`
                                            w-full flex items-center justify-between gap-2
                                            px-3 py-2 rounded-lg
                                            transition-all duration-200 font-medium 
                                            text-sm
                                            border shadow-sm hover:shadow-md
                                            bg-white text-gray-700 border-gray-300 
                                            hover:bg-gradient-to-br hover:from-emerald-50 hover:to-emerald-100 hover:border-emerald-400
                                        `}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Filter className="w-4 h-4 text-gray-400" />
                                            <span>
                                                {filtros.categoria 
                                                    ? CATEGORIAS.find(cat => cat.value === filtros.categoria)?.label 
                                                    : 'Todas'}
                                            </span>
                                        </div>
                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                    </button>
                                }
                                items={CATEGORIAS.map(cat => ({
                                    id: cat.value,
                                    label: cat.label,
                                    icon: Filter,
                                    iconColor: filtros.categoria === cat.value ? 'text-emerald-600' : 'text-gray-600',
                                    onClick: () => setFiltros({ ...filtros, categoria: cat.value })
                                }))}
                                position="bottom-left"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 items-center">
                        {/* Botão de Período */}
                        <PeriodoFilterButton
                            periodo={periodo}
                            startDate={startDate}
                            endDate={endDate}
                            onPeriodoChange={handlePeriodoChange}
                            onDateRangeChange={handleDateRangeChange}
                        />

                        <PrimaryButton
                            onClick={() => setIsUploadModalOpen(true)}
                            icon={<Upload className="w-4 h-4" />}
                        >
                            Upload Documento
                        </PrimaryButton>
                    </div>
                </div>


                {/* Segunda linha: Ordenação e View Mode */}
                {documentosFiltrados.length > 0 && (
                    <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center pt-2 border-t border-gray-200">
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 font-medium">
                                {documentosFiltrados.length} {documentosFiltrados.length === 1 ? 'documento' : 'documentos'}
                            </span>
                            <div className="h-4 w-px bg-gray-300" />
                            {/* Ordenação */}
                            <div className="flex items-center gap-2">
                                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                                <select
                                    value={ordenacao}
                                    onChange={(e) => setOrdenacao(e.target.value)}
                                    className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                                >
                                    {ORDENACOES.map(ord => (
                                        <option key={ord.value} value={ord.value}>{ord.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Toggle View Mode */}
                        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded transition-colors ${
                                    viewMode === 'grid'
                                        ? 'bg-white text-emerald-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                                title="Visualização em grade"
                            >
                                <Grid3x3 className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded transition-colors ${
                                    viewMode === 'list'
                                        ? 'bg-white text-emerald-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                                title="Visualização em lista"
                            >
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Lista de documentos */}
            {documentosFiltrados.length === 0 ? (
                <Card className="p-12 text-center">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-2">
                        {documentos.length === 0 ? 'Nenhum documento cadastrado' : 'Nenhum documento encontrado'}
                    </p>
                    <p className="text-gray-400 text-sm mb-4">
                        {documentos.length === 0 
                            ? 'Faça upload do primeiro documento do paciente'
                            : 'Tente ajustar os filtros de busca'}
                    </p>
                    {documentos.length === 0 && (
                        <PrimaryButton onClick={() => setIsUploadModalOpen(true)}>
                            <Upload className="w-4 h-4" />
                            Fazer Upload
                        </PrimaryButton>
                    )}
                </Card>
            ) : (
                <>
                    {viewMode === 'grid' ? (
                        // Visualização em Grid
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {documentosFiltrados.map((documento) => (
                                <Card key={documento.id} className="p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 bg-emerald-100 p-3 rounded-lg">
                                            <File className="w-6 h-6 text-emerald-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-semibold text-gray-900 truncate flex-1">
                                                    {documento.titulo}
                                                </h3>
                                                {documento.entidade_origem && (
                                                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full" title={`Vinculado a: ${getEntidadeOrigemLabel(documento.entidade_origem)}`}>
                                                        <Link2 className="w-3 h-3" />
                                                        Vinculado
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 mb-2">
                                                {getCategoriaLabel(documento.categoria)}
                                            </p>
                                            <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                                                <span>{formatDate(documento.data_upload)}</span>
                                                <span>•</span>
                                                <span>{formatFileSize(documento.tamanho_bytes)}</span>
                                            </div>
                                            {documento.descricao && (
                                                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                                    {documento.descricao}
                                                </p>
                                            )}
                                            <div className="flex gap-2 flex-wrap">
                                                <button
                                                    onClick={() => handleView(documento)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                    title="Visualizar"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDownload(documento)}
                                                    className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                                                    title="Download"
                                                >
                                                    <Download className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenEdit(documento)}
                                                    className="p-2 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                                                    title="Editar"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenDelete(documento)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                    title="Excluir"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        // Visualização em Lista (Tabela)
                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Documento
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Categoria
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Data Upload
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Tamanho
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Ações
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {documentosFiltrados.map((documento) => (
                                            <tr key={documento.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-shrink-0 bg-emerald-100 p-2 rounded">
                                                            <File className="w-5 h-5 text-emerald-600" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-sm font-medium text-gray-900 truncate flex-1">
                                                                    {documento.titulo}
                                                                </p>
                                                                {documento.entidade_origem && (
                                                                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex-shrink-0" title={`Vinculado a: ${getEntidadeOrigemLabel(documento.entidade_origem)}`}>
                                                                        <Link2 className="w-3 h-3" />
                                                                        Vinculado
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {documento.descricao && (
                                                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                                                    {documento.descricao}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-600">
                                                        {getCategoriaLabel(documento.categoria)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-600">
                                                        {formatDate(documento.data_upload)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-600">
                                                        {formatFileSize(documento.tamanho_bytes)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleView(documento)}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                            title="Visualizar"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDownload(documento)}
                                                            className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                                                            title="Download"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenEdit(documento)}
                                                            className="p-2 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                                                            title="Editar"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenDelete(documento)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            title="Excluir"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}
                </>
            )}

            {/* Modais */}
            <UploadDocumentoModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onUpload={handleUpload}
            />

            {selectedDocumento && (
                <>
                    <EditarDocumentoModal
                        isOpen={isEditModalOpen}
                        onClose={() => {
                            setIsEditModalOpen(false);
                            setSelectedDocumento(null);
                        }}
                        documento={selectedDocumento}
                        onSave={handleEdit}
                    />

                    <Modal
                        isOpen={isViewModalOpen}
                        onClose={() => {
                            setIsViewModalOpen(false);
                            setSelectedDocumento(null);
                        }}
                        title={selectedDocumento.titulo}
                        size="2xl"
                    >
                        <div className="w-full h-[600px]">
                            <iframe
                                src={visualizarDocumentoPaciente(pacienteId, selectedDocumento.id)}
                                className="w-full h-full border-0 rounded-lg"
                                title={selectedDocumento.titulo}
                                referrerPolicy="no-referrer"
                            />
                        </div>
                        <div className="mt-4 flex justify-end">
                            <PrimaryButton
                                onClick={() => handleDownload(selectedDocumento)}
                                variant="secondary"
                            >
                                <Download className="w-4 h-4" />
                                Baixar Documento
                            </PrimaryButton>
                        </div>
                    </Modal>

                    <ConfirmacaoModal
                        isOpen={isDeleteModalOpen}
                        onClose={() => {
                            setIsDeleteModalOpen(false);
                            setSelectedDocumento(null);
                        }}
                        onConfirm={handleDelete}
                        titulo="Confirmar Exclusão"
                        mensagem={`Tem certeza que deseja excluir o documento **${selectedDocumento.titulo}**? Esta ação não pode ser desfeita.`}
                        confirmText="Excluir"
                        cancelText="Cancelar"
                    />
                </>
            )}
        </div>
    );
};

export default PacienteDocumentacaoTab;

